import { LitElement, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { store } from '../store.js';
import type { MessageKey } from '../i18n/messages.js';
import { pageStyles } from '../styles/page.js';
import {
  heroNetwork,
  teaserMali,
  teaserJuvo,
  teaserDental,
  iconClinic,
  iconPeople,
  iconDevices,
  iconGlobe,
  iconHeart,
  iconSun,
  iconVolunteer,
  iconBriefcase,
} from '../illustrations.js';
import '@healthgeek/ui';

@customElement('hg-home-page')
export class HgHomePage extends LitElement {
  static styles = [pageStyles];

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  private unsubscribe?: () => void;
  private t(key: MessageKey) {
    return store.t(key);
  }

  render() {
    const t = this.t.bind(this);
    return html`
      <section class="hero">
        <div class="wrap-wide hero-grid">
          <div>
            <div class="kicker">${t('hero.pill')}</div>
            <blockquote class="lead">
              <p>“${t('hero.quote')}”</p>
              <div class="attr">${t('hero.attr')}</div>
            </blockquote>
            <div class="hero-actions">
              <hg-button href="/programs">${t('hero.cta.programs')}</hg-button>
              <hg-button href="/story" variant="outlined">${t('hero.cta.charter')}</hg-button>
            </div>
          </div>
          <div class="illus-card">
            ${heroNetwork}
            <div class="illus-caption">${t('hero.illus.caption')}</div>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="wrap-wide">
          <div class="section-num">${t('impact.kicker')}</div>
          <h2>${t('impact.title')}</h2>
          <p>${t('impact.lede')}</p>
          <div class="counters-grid">
            <div class="counter-card">
              <div class="chip forest">${iconClinic}</div>
              <div class="num">8</div>
              <div class="label">${t('impact.clinics.label')}</div>
            </div>
            <div class="counter-card">
              <div class="chip coral">${iconPeople}</div>
              <div class="num">12</div>
              <div class="label">${t('impact.chws.label')}</div>
            </div>
            <div class="counter-card">
              <div class="chip sky">${iconDevices}</div>
              <div class="num">30+</div>
              <div class="label">${t('impact.local.label')}</div>
            </div>
            <div class="counter-card">
              <div class="chip amber">${iconGlobe}</div>
              <div class="num">2</div>
              <div class="label">${t('impact.regions.label')}</div>
            </div>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="wrap-wide">
          <div class="section-num">${t('programs.section')}</div>
          <h2>${t('programs.title')}</h2>
          <div class="program-teaser">
            <a class="teaser-card" href="/programs#mali">
              ${teaserMali}
              <div class="teaser-body">
                <div class="teaser-tag">${t('programs.mali.tag')}</div>
                <h3>${t('programs.mali.title')}</h3>
                <p>${t('programs.mali.teaser')}</p>
              </div>
            </a>
            <a class="teaser-card" href="/programs#juvo">
              ${teaserJuvo}
              <div class="teaser-body">
                <div class="teaser-tag">${t('programs.juvo.tag')}</div>
                <h3>${t('programs.juvo.title')}</h3>
                <p>${t('programs.juvo.teaser')}</p>
              </div>
            </a>
            <a class="teaser-card" href="/programs#dental-intelligence">
              ${teaserDental}
              <div class="teaser-body">
                <div class="teaser-tag">${t('programs.di.tag')}</div>
                <h3>${t('programs.di.title')}</h3>
                <p>${t('programs.di.teaser')}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="wrap-wide">
          <div class="section-num">${t('help.section')}</div>
          <h2>${t('help.title')}</h2>
          <p>${t('help.lede')}</p>
          <ol class="ways">
            <li>
              <div class="chip forest">${iconHeart}</div>
              <div>
                <h3>${t('help.donate.title')}</h3>
                <p>${t('help.donate.body')}</p>
              </div>
            </li>
            <li>
              <div class="chip coral">${iconSun}</div>
              <div>
                <h3>${t('help.monthly.title')}</h3>
                <p>${t('help.monthly.body')}</p>
              </div>
            </li>
            <li>
              <div class="chip sky">${iconVolunteer}</div>
              <div>
                <h3>${t('help.geek.title')}</h3>
                <p>${t('help.geek.body')}</p>
              </div>
            </li>
            <li>
              <div class="chip amber">${iconBriefcase}</div>
              <div>
                <h3>${t('help.partner.title')}</h3>
                <p>${t('help.partner.body')}</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section class="closing">
        <div class="wrap">
          <p class="lead">“${t('cta.title')}”</p>
          <div class="closing-actions">
            <hg-button href="/contact#donate">${t('cta.donate')}</hg-button>
            <hg-button href="/programs" variant="outlined">${t('hero.cta.programs')}</hg-button>
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hg-home-page': HgHomePage;
  }
}
